import {memo} from 'react';
import type {ElementType} from 'react';
import Typography from '@mui/material/Typography';
import type {SxProps, Theme} from '@mui/material/styles';
import {colors, fontFamily, fontSize, fontWeight, letterSpacing, lineHeight, spacing} from '@/styles/tokens';

interface EyebrowProps {
    /** Kicker text rendered above section headings. Always plain text — every consumer passes a string. */
    text: string;
    /**
     * Rendered element. Defaults to a paragraph. `span` is needed by the one bespoke
     * inline-badge eyebrow (drawpost case study) that supplies its own wrapper and spacing.
     */
    component?: ElementType;
    /** Extra sx merged on top of the default styling (e.g. `textAlign`, or an `mb` override). */
    sx?: SxProps<Theme>;
    /** Render for a dark background — switches the slate color to the on-dark orange. */
    onDark?: boolean;
}

const eyebrowSx: SxProps<Theme> = {
    fontFamily: fontFamily.heading,
    fontSize: fontSize[14],
    lineHeight: lineHeight[20],
    fontWeight: fontWeight.semibold,
    letterSpacing: letterSpacing.eyebrow,
    textTransform: 'uppercase',
    color: colors.text.secondary,
    mb: spacing[16]
};

/**
 * Eyebrow (Atom) — the small uppercase kicker rendered above section headings.
 * The single source of truth for eyebrow styling site-wide (Headers font, 14px
 * size, 20px line-height, 600 weight, 0.8px tracking, slate-gray color, 16px
 * bottom margin) as component-owned `sx`.
 */
const Eyebrow = memo(function Eyebrow({text, component = 'p', sx, onDark}: EyebrowProps) {
    return (
        <Typography component={component} sx={{...eyebrowSx, ...(onDark && {color: colors.neutral.slate400}), ...sx}}>
            {text}
        </Typography>
    );
});

export default Eyebrow;
